import { Controller } from "@hotwired/stimulus"

// Stimulus controller for the settings form (the element gets
// data-controller="settings-form"). Ported, in vanilla JS, from the
// `#settings_form` block of the legacy initPage() in
// app/javascript/entrypoints/application.js.
//
// It wires a single delegated click listener on the form element handling:
//   - add/remove rows for the four dynamic field groups (check-in devices,
//     car-testing devices, car-testing cars, custody devices)
//   - #validate_json — JSON.parse the CodeMirror 6 doc and alert the result
//   - #generate_random_code — insert a 16-char [a-z0-9] code at the cursor
//
// The CodeMirror content is read via the view stored on the textarea by the
// code-editor controller: document.getElementById('setting_content')._cmView.
//
export default class extends Controller {
  connect() {
    this.onClick = this.handleClick.bind(this)
    this.element.addEventListener("click", this.onClick)
  }

  disconnect() {
    if (this.onClick) {
      this.element.removeEventListener("click", this.onClick)
      this.onClick = null
    }
  }

  handleClick(event) {
    const target = event.target

    // --- Check-in devices -------------------------------------------------
    if (target.closest(".add-checkin-device-fields")) {
      event.preventDefault()
      this.addClonedRow(".checkin-device-fields", (clone) => {
        this.clearValue(clone, ".device-name-field")
        this.clearValue(clone, ".device-token-field")
      })
      return
    }
    if (target.closest(".remove-checkin-device")) {
      event.preventDefault()
      this.removeRow(target, ".checkin-device-fields")
      return
    }

    // --- Car-testing devices ----------------------------------------------
    if (target.closest(".add-car-testing-device-fields")) {
      event.preventDefault()
      this.addClonedRow(".car-testing-device-fields", (clone) => {
        this.clearValue(clone, ".device-name-field")
        this.clearValue(clone, ".device-token-field")
      })
      return
    }
    if (target.closest(".remove-car-testing-device")) {
      event.preventDefault()
      this.removeRow(target, ".car-testing-device-fields")
      return
    }

    // --- Car-testing cars -------------------------------------------------
    if (target.closest(".add-car-testing-car-fields")) {
      event.preventDefault()
      this.addClonedRow(".car-testing-car-fields", (clone) => {
        // car-number = the new total count of car rows (matches legacy behaviour).
        const count = document.querySelectorAll(
          ".car-testing-cars .car-testing-car-fields"
        ).length
        this.setValue(clone, ".car-number-field", count)
        this.clearValue(clone, ".car-name-field")
        this.clearValue(clone, ".car-licence-number-field")
        this.clearValue(clone, ".car-token-field")
      })
      return
    }
    if (target.closest(".remove-car-testing-car")) {
      event.preventDefault()
      this.removeRow(target, ".car-testing-car-fields")
      return
    }

    // --- Custody devices --------------------------------------------------
    if (target.closest(".add-custody-device-fields")) {
      event.preventDefault()
      this.addClonedRow(".custody-device-fields", (clone) => {
        this.clearValue(clone, ".device-name-field")
        this.clearValue(clone, ".device-token-field")
      })
      return
    }
    if (target.closest(".remove-custody-device")) {
      event.preventDefault()
      this.removeRow(target, ".custody-device-fields")
      return
    }

    // --- Validate JSON ----------------------------------------------------
    if (target.closest("#validate_json")) {
      event.preventDefault()
      this.validateJson()
      return
    }

    // --- Generate random code ---------------------------------------------
    if (target.closest("#generate_random_code")) {
      event.preventDefault()
      this.generateRandomCode()
      return
    }
  }

  // Clone the first matching field group, insert after the last, then run the
  // caller's mutation on the clone. Mirrors the jQuery `:first` clone +
  // `insertAfter(':last')` pattern.
  addClonedRow(selector, mutate) {
    const rows = this.element.querySelectorAll(selector)
    if (rows.length === 0) return

    const first = rows[0]
    const last = rows[rows.length - 1]
    const clone = first.cloneNode(true)

    last.insertAdjacentElement("afterend", clone)

    if (typeof mutate === "function") {
      mutate(clone)
    }
  }

  // Remove the closest matching field group to the clicked element, but only
  // if more than one row remains (matches the legacy guard).
  removeRow(target, selector) {
    const rows = this.element.querySelectorAll(selector)
    if (rows.length > 1) {
      const row = target.closest(selector)
      if (row) row.remove()
    }
  }

  clearValue(scope, selector) {
    const field = scope.querySelector(selector)
    if (field) field.value = ""
  }

  setValue(scope, selector, value) {
    const field = scope.querySelector(selector)
    if (field) field.value = value
  }

  validateJson() {
    const editorTextarea = document.getElementById("setting_content")
    const view = editorTextarea && editorTextarea._cmView
    if (!view) {
      alert("CodeMirror not detected") // nosemgrep: javascript.lang.best-practice.leftover_debugging.javascript-alert
      return
    }

    const content = view.state.doc.toString()
    try {
      JSON.parse(content)
      alert("Valid JSON") // nosemgrep: javascript.lang.best-practice.leftover_debugging.javascript-alert
    } catch (e) {
      alert("Invalid JSON: " + e) // nosemgrep: javascript.lang.best-practice.leftover_debugging.javascript-alert
    }
  }

  generateRandomCode() {
    const editorTextarea = document.getElementById("setting_content")
    const view = editorTextarea && editorTextarea._cmView
    if (!view) {
      alert("CodeMirror not detected") // nosemgrep: javascript.lang.best-practice.leftover_debugging.javascript-alert
      return
    }

    const characters = "abcdefghijklmnopqrstuvwxyz0123456789"
    let code = ""
    for (let i = 0; i < 16; i++) {
      const index = Math.floor(Math.random() * characters.length) // nosemgrep: nodejs_scan.javascript-crypto-rule-node_insecure_random_generator, ajinabraham.njsscan.crypto.crypto_node.node_insecure_random_generator
      code += characters[index]
    }

    const head = view.state.selection.main.head
    view.dispatch({ changes: { from: head, insert: code } })
    view.focus()
  }
}
